// AgentVerse/client/components/MessagingContactsCrud.js
import { ComponentBase } from "../core/ComponentBase.js";
import { z } from "zod";

export class MessagingContactsCrud extends ComponentBase {
    constructor(config) {
        super(config);
        this.dataCrud = null;
        this.eventGroup = this.config.instanceId || "messagingContactsCrud";
        this.currentProjectId = null;
    }

    async initialize() {
        await this.initializeDataCrud();
        await super.initialize();
    }

    async initializeDataCrud() {
        if (this.dataCrud && typeof this.dataCrud.destroy === 'function') {
            this.dataCrud.destroy();
            this.dataCrud = null;
        }

        const dataProviderContext = {};
        dataProviderContext.projectId = this.currentProjectId; // Can be null

        this.dataCrud = await this.createManagedComponent("DataCrud", null, {
            eventGroup: `${this.eventGroup}-dc`,
            appClient: this.appClient,
            rpcClass: "MessagingContactsRpc",
            schemaPath: "collections/messagingContacts",
            title: this.currentProjectId ? "Project Messaging Contacts" : "Global Messaging Contacts",
            layout: 'horizontal',
            layoutConfig: { formWidth: '600px' },
            viewConfig: {
                mode: 'grid',
                fields: [
                    { name: 'displayName', label: 'Display Name' },
                    { name: 'contactId', label: 'Contact ID' }, // Still show in view
                    { name: 'contactType', label: 'Type' },
                    { name: 'projectId', label: 'Project ID', render: v => v || 'Global' }, // Show if global
                    { name: 'isArchived', label: 'Archived', render: v => v ? 'Yes' : 'No' },
                    {
                        name: 'channels', label: 'Channels',
                        render: (channels) => {
                            if (!channels || channels.length === 0) return 'None';
                            return channels.map(c => `${c.channelType} (${c.identifier})${c.isPreferred ? ' <i class="bi bi-check-circle-fill text-success" title="Preferred"></i>' : ''}`).join('<br>');
                        }
                    },
                ],
                filter: {
                    enabled: true,
                    schema: z.object({
                        displayName: z.string().optional(),
                        contactId: z.string().optional(),
                        contactType: z.string().optional(),
                        isArchived: z.string().optional(),
                    }),
                    fields: {
                        displayName: { type: 'text', label: 'Filter by Display Name' },
                        contactId: { type: 'text', label: 'Filter by Contact ID' },
                        contactType: {
                            type: 'select', label: 'Filter by Type',
                            options: [
                                { value: '', label: 'All Types' },
                                { value: 'internal_user', label: 'Internal User' },
                                { value: 'external_contact', label: 'External Contact' },
                                { value: 'agent', label: 'Agent' }
                            ]
                        },
                        isArchived: { type: 'select', label: 'Filter by Archived', options: [{ value: '', label: 'Any' }, { value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }] }
                    }
                },
            },
            formConfig: {
                mode: 'edit',
                fields: {
                    // contactId is removed - server will generate
                    displayName: { type: 'text', label: 'Display Name' },
                    contactType: {
                        type: 'select', label: 'Contact Type',
                        options: [
                            { value: 'internal_user', label: 'Internal User (Links to App User)' },
                            { value: 'external_contact', label: 'External Contact' },
                            { value: 'agent', label: 'AI Agent' }
                        ]
                    },
                    appUserId: {
                        type: 'reference', label: 'App User (if Internal)',
                        reference: { collection: 'users', valueField: '_id', displayField: 'username' },
                        visibleWhen: (formData) => formData.contactType === 'internal_user'
                    },
                    agentName: {
                        type: 'reference', label: 'Agent Name (if Agent)',
                        // Filter agents by current project OR global agents (projectId is null)
                        reference: {
                            collection: 'agentConfigs',
                            valueField: 'name',
                            displayField: 'name',
                            filter: {},
                        },
                        visibleWhen: (formData) => formData.contactType === 'agent',
                        placeholder: 'Select the configured agent'
                    },
                    projectId: {
                        type: 'text',
                        label: 'Project ID (Leave blank for Global)',
                        placeholder: 'Optional: Associate with a specific project',
                        // This field might be hidden or readonly depending on context
                        // visibleWhen: () => !this.currentProjectId // Example: only show if no project selected
                    },
                    channels: {
                        type: 'array',
                        label: 'Additional Channels', // Renamed as default channels are auto-added
                        item: {
                            fields: {
                                channelType: {
                                    type: 'select', label: 'Channel Type',
                                    options: [

                                        { value: 'app_direct', label: 'App User' },
                                        { value: 'agent_internal', label: 'AI Agent' },
                                        { value: 'whatsapp', label: 'WhatsApp' },
                                        { value: 'sms', label: 'SMS' },
                                        { value: 'email', label: 'Email' }
                                    ]
                                },
                                identifier: { type: 'text', label: 'Identifier (e.g., Phone #, Email Address)' },
                                isPreferred: { type: 'boolean', label: 'Preferred' },
                                canReceive: { type: 'boolean', label: 'Can Receive', default: true },
                                canSend: { type: 'boolean', label: 'Can Send', default: true },
                            }
                        }
                    },
                    avatarUploadKey: {
                        type: 'FileUpload',
                        label: 'Avatar (Optional)',
                        getUploadKey: async (itemData) => { // itemData is the current form data
                            return await this.appClient.rpcClient.call('MessagingContactsRpc.getContactAvatarUploadKey',
                                { contact_Id: itemData?._id } // Pass contact's DB _id if editing
                            );
                        },
                        // You might want to show the current avatar if editing:
                        // This requires FileUpload to support displaying an initial image, or a separate display field.
                        // For now, FileUpload will just show the upload UI.
                    },

                    notes: { type: 'textarea', label: 'Admin Notes (Optional)', rows: 3 },
                    isArchived: { type: 'boolean', label: 'Archived', default: false }
                },
                sections: [
                    { title: "Core Information", fields: ["displayName", "contactType", "appUserId", "agentName", "avatarUploadKey", "projectId"] },
                    { title: "Additional Channels", fields: ["channels"] },
                    { title: "Administrative", fields: ["notes", "isArchived"] }
                ]
            },
            dataConfig: {
                autoLoad: false, // Auto-load will be triggered by project selection
                pageSize: 20,
                context: dataProviderContext,
            },
            defaultValues: {
                avatarUrl: null
            }
        });
    }



    async initAfterLogin() {
        this.dataCrud.refreshData();
    }


    async render() {
        if (!this.container) {
            this.container = document.createElement('div');
            this.container.className = 'messaging-contacts-crud-container';
        }
        this.container.innerHTML = '';
        this.container.appendChild(await this.dataCrud.render());
        return this.container;
    }

    destroy() {
        this.dataCrud?.destroy();
        this.dataCrud = null;
        super.destroy();
    }
}